/**
 * PartnerPricingWizardPage — /partner/pricing-wizard (Phase K).
 *
 * 3-step wizard, 2-bucket engine preserved:
 *   Step 1 — Pricing: 4 rows shown, only `1 dog` (base) and
 *            `additional dog rate` are EDITABLE. The 2/3/4+ rows are
 *            DISPLAY-ONLY (computed from base + additional × (n-1)).
 *            Bounds sourced from server payload — never hardcoded FE.
 *   Step 2 — Tier: Standard / Preferred / Elite cards. Selection is
 *            intent-only (matches tier_selection.py contract).
 *   Step 3 — Review + Confirm. Standard = auto-promote (immediate
 *            partner_is_fully_live=true). Preferred/Elite = create
 *            subscription intent row, admin manually promotes.
 *
 * Copy discipline: Family H bans still apply. Partner-facing tier
 * percentages ARE allowed here (partners need to know their rate).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Check,
  Shield,
  Sparkles,
  Star,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";

const _REFETCH_DEBOUNCE_MS = 300;

/* Tier cards — pretty labels + iconography. Commission + cap values
 * come from the server payload, never hardcoded here. */
const TIER_CARDS = [
  {
    id: "standard",
    label: "Standard",
    icon: Shield,
    color: "#6b7280",
    tagline: "Free tier. Perfect for solo PooPartners easing in.",
  },
  {
    id: "preferred",
    label: "Preferred",
    icon: Sparkles,
    color: "#7c3aed",
    tagline: "For pros scaling into more zips + repeat customers.",
  },
  {
    id: "elite",
    label: "Elite",
    icon: Star,
    color: "#F89623",
    tagline: "Metro-wide coverage, lowest commission cut.",
  },
];

function centsToDollars(c) {
  return typeof c === "number" ? (c / 100).toFixed(2) : "";
}

/* Preview scenarios — the 3 sample bookings shown in Step 1. */
const _PREVIEW_SCENARIOS = [
  { dog_count: 1, frequency: "weekly", label: "1 dog · weekly" },
  { dog_count: 2, frequency: "just_once", label: "2 dogs · one-time" },
  { dog_count: 3, frequency: "twice_weekly", label: "3 dogs · twice-weekly" },
];

/* ------------------------------------------------------------------ */
export default function PartnerPricingWizardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wizard state — cents everywhere until display.
  const [step, setStep] = useState(1);
  const [baseCents, setBaseCents] = useState(2000);
  const [additionalCents, setAdditionalCents] = useState(700);
  const [selectedTierId, setSelectedTierId] = useState("standard");

  // Preview earnings state.
  const [previewRows, setPreviewRows] = useState(null);
  const [previewErrored, setPreviewErrored] = useState(false);

  // Confirm.
  const [confirming, setConfirming] = useState(false);

  // Debounce ref for preview refetch.
  const debounceRef = useRef(null);

  /* -------------------- LOAD -------------------- */
  const load = useCallback(async () => {
    try {
      const [meRes, tiersRes, cfgRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/partner/tiers/public"),
        api.get("/config/pricing"),
      ]);
      setMe(meRes.data.user);
      setTiers(tiersRes.data.tiers || tiersRes.data.items || tiersRes.data || []);
      // Bounds MUST come from server payload — Family E-style hardcoding
      // ban means no fallback constants here. If the server didn't
      // return `partner_pricing_bounds`, the wizard treats bounds as
      // unavailable and stays in a loading/error state until the caller
      // refreshes.
      const cfg = cfgRes.data;
      const serverBounds = cfg?.partner_pricing_bounds || null;
      if (serverBounds) {
        setBounds(serverBounds);
        setBaseCents(serverBounds.base_price_cents?.default ?? 2000);
        setAdditionalCents(serverBounds.additional_dog_price_cents?.default ?? 700);
      }
      // else: bounds stay null → renders the loading placeholder.
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* -------------------- PREVIEW REFETCH -------------------- */
  useEffect(() => {
    if (step !== 1) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await Promise.all(
          _PREVIEW_SCENARIOS.map((sc) =>
            api
              .post("/partner/pricing-wizard/preview", {
                base_price_cents: baseCents,
                additional_dog_price_cents: additionalCents,
                dog_count: sc.dog_count,
                frequency: sc.frequency,
              })
              .then((r) => ({
                ...sc,
                provider_take_amount_cents: r.data.provider_take_amount_cents,
                total_amount_cents: r.data.total_amount_cents,
              }))
              .catch(() => null),
          ),
        );
        const settled = results.filter((r) => r !== null);
        if (settled.length === 0) {
          setPreviewErrored(true);
          setPreviewRows(null);
        } else {
          setPreviewErrored(false);
          setPreviewRows(settled);
        }
      } catch {
        setPreviewErrored(true);
      }
    }, _REFETCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [baseCents, additionalCents, step]);

  /* -------------------- DERIVED PRICING -------------------- */
  const pricingRows = useMemo(() => {
    const base = baseCents / 100;
    const additional = additionalCents / 100;
    return [
      { dogs: "1 dog",   totalDollars: base,             editable: "base" },
      { dogs: "2 dogs",  totalDollars: base + additional * 1, editable: null },
      { dogs: "3 dogs",  totalDollars: base + additional * 2, editable: null },
      { dogs: "4+ dogs", totalDollars: base + additional * 3, editable: null,
        caption: `Each additional dog beyond 4 adds $${additional.toFixed(2)}` },
    ];
  }, [baseCents, additionalCents]);

  const selectedTier = tiers.find((t) => t.id === selectedTierId);

  /* -------------------- STEP GATES -------------------- */
  const step1Complete =
    bounds &&
    baseCents >= bounds.base_price_cents.floor &&
    baseCents <= bounds.base_price_cents.ceiling &&
    additionalCents >= bounds.additional_dog_price_cents.floor &&
    additionalCents <= bounds.additional_dog_price_cents.ceiling;

  const step2Complete = !!selectedTier;

  /* -------------------- CONFIRM -------------------- */
  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/partner/pricing-wizard/confirm", {
        base_price_cents: baseCents,
        additional_dog_price_cents: additionalCents,
        selected_tier_id: selectedTierId,
      });
      // Commit B — §4.3 partner funnel end. Fires on genuine
      // confirm-success only (before the tier-activation branch).
      trackEvent("partner.application_submit", {
        selected_tier_id: selectedTierId,
      });
      if (data.partner_is_fully_live) {
        toast.success("You're a Standard-tier PooPartner. Start plucking!");
      } else {
        toast.success(
          "Onboarding complete. Awaiting tier activation — you'll get an email.",
        );
      }
      // Redirect to partner dashboard.
      setTimeout(() => navigate("/dashboard/provider"), 800);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg =
        (d && typeof d === "object" && d.message) ||
        formatApiErrorDetail(d) ||
        "Confirm failed.";
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  /* -------------------- RENDER -------------------- */
  const container = {
    maxWidth: 960,
    margin: "0 auto",
    padding: "40px 20px 80px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  };

  if (loading || !bounds) {
    return (
      <div style={container} data-testid="partner-pricing-wizard-loading">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  return (
    <div style={container} data-testid="partner-pricing-wizard-page">
      <FloatingThemeToggle />
      <header style={{ marginBottom: 20 }}>
        <span
          style={{
            color: "#F89623",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 1.5,
            textTransform: "uppercase",
          }}
          data-testid="partner-pricing-wizard-eyebrow"
        >
          Pricing wizard
        </span>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            margin: "8px 0 8px 0",
            color: "#374751",
          }}
          data-testid="partner-pricing-wizard-heading"
        >
          Set your pricing, pick your tier
        </h1>
      </header>

      <StepIndicator step={step} />

      {step === 1 && (
        <Step1Pricing
          bounds={bounds}
          baseCents={baseCents}
          setBaseCents={setBaseCents}
          additionalCents={additionalCents}
          setAdditionalCents={setAdditionalCents}
          pricingRows={pricingRows}
          previewRows={previewRows}
          previewErrored={previewErrored}
        />
      )}

      {step === 2 && (
        <Step2Tier
          tiers={tiers}
          selectedTierId={selectedTierId}
          setSelectedTierId={setSelectedTierId}
        />
      )}

      {step === 3 && (
        <Step3Review
          pricingRows={pricingRows}
          selectedTier={selectedTier}
          confirming={confirming}
          onConfirm={confirm}
        />
      )}

      <div
        style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}
      >
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          data-testid="partner-pricing-wizard-back-btn"
        >
          <ArrowLeft size={16} style={{ marginRight: 6 }} /> Back
        </Button>
        {step < 3 && (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={(step === 1 && !step1Complete) || (step === 2 && !step2Complete)}
            data-testid="partner-pricing-wizard-next-btn"
          >
            Next <ArrowRight size={16} style={{ marginLeft: 6 }} />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function StepIndicator({ step }) {
  const steps = ["Pricing", "Tier", "Review"];
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginBottom: 24,
      }}
      data-testid="partner-pricing-wizard-step-indicator"
    >
      {steps.map((s, i) => {
        const active = step === i + 1;
        const done = step > i + 1;
        return (
          <div
            key={s}
            style={{
              flex: 1,
              padding: "10px 12px",
              borderRadius: 10,
              background: active
                ? "#FFF8EE"
                : done
                  ? "#ECFDF5"
                  : "#F9FAFB",
              border: `1px solid ${active ? "#F89623" : done ? "#065f46" : "#e5e7eb"}`,
              color: active ? "#8B5F0E" : done ? "#065f46" : "#4b5563",
              fontWeight: 600,
              textAlign: "center",
              fontSize: 14,
            }}
          >
            {done && <Check size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />}
            {`${i + 1}. ${s}`}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Step1Pricing({
  bounds,
  baseCents,
  setBaseCents,
  additionalCents,
  setAdditionalCents,
  pricingRows,
  previewRows,
  previewErrored,
}) {
  const baseBounds = bounds.base_price_cents;
  const addBounds = bounds.additional_dog_price_cents;
  return (
    <Card
      style={{ border: "1px solid #e5e7eb", borderRadius: 16 }}
      data-testid="partner-pricing-wizard-step-1"
    >
      <CardHeader>
        <CardTitle
          style={{ display: "flex", gap: 8, alignItems: "center", color: "#374751" }}
        >
          <DollarSign size={20} color="#F89623" /> Set your rates
        </CardTitle>
      </CardHeader>
      <CardContent style={{ display: "grid", gap: 20 }}>
        {/* Two editable inputs */}
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
          <div>
            <Label htmlFor="base-price">1-dog rate</Label>
            <Input
              id="base-price"
              type="number"
              min={baseBounds.floor / 100}
              max={baseBounds.ceiling / 100}
              step="0.50"
              value={(baseCents / 100).toFixed(2)}
              onChange={(e) => {
                const v = parseFloat(e.target.value || "0");
                setBaseCents(Math.round(v * 100));
              }}
              data-testid="partner-pricing-wizard-base-input"
            />
            <p
              style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}
              data-testid="partner-pricing-wizard-base-range"
            >
              Platform range: ${(baseBounds.floor / 100).toFixed(2)} to ${(baseBounds.ceiling / 100).toFixed(2)}
            </p>
          </div>
          <div>
            <Label htmlFor="additional-price">Per additional dog</Label>
            <Input
              id="additional-price"
              type="number"
              min={addBounds.floor / 100}
              max={addBounds.ceiling / 100}
              step="0.25"
              value={(additionalCents / 100).toFixed(2)}
              onChange={(e) => {
                const v = parseFloat(e.target.value || "0");
                setAdditionalCents(Math.round(v * 100));
              }}
              data-testid="partner-pricing-wizard-additional-input"
            />
            <p
              style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}
              data-testid="partner-pricing-wizard-additional-range"
            >
              Platform range: ${(addBounds.floor / 100).toFixed(2)} to ${(addBounds.ceiling / 100).toFixed(2)}
            </p>
          </div>
        </div>

        {/* 4-row derived pricing */}
        <div>
          <p style={{ fontSize: 13, color: "#374751", fontWeight: 600, marginBottom: 8 }}>
            Your per-visit rate:
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            }}
            data-testid="partner-pricing-wizard-derived-rows"
          >
            {pricingRows.map((row) => (
              <div
                key={row.dogs}
                style={{
                  padding: "10px 12px",
                  background: row.editable ? "#FFF8EE" : "#F9FAFB",
                  border: `1px solid ${row.editable ? "#F89623" : "#e5e7eb"}`,
                  borderRadius: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                data-testid={`partner-pricing-wizard-row-${row.dogs.replace(/[+\s]+/g, "_")}`}
              >
                <span style={{ color: "#374751" }}>{row.dogs}</span>
                <span style={{ fontWeight: 700, color: "#374751" }}>
                  ${row.totalDollars.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          {pricingRows[3].caption && (
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              {pricingRows[3].caption}
            </p>
          )}
          <p
            style={{
              fontSize: 13,
              color: "#4b5563",
              marginTop: 12,
              lineHeight: 1.5,
            }}
          >
            Every extra dog adds the same amount to your rate. Bigger packs =
            better per-dog value for customers, more per-visit pay for you.
          </p>
        </div>

        {/* Preview strip */}
        <div>
          <p style={{ fontSize: 13, color: "#374751", fontWeight: 600, marginBottom: 8 }}>
            Sample bookings at your pricing:
          </p>
          <div
            style={{
              display: "grid",
              gap: 8,
            }}
            data-testid="partner-pricing-wizard-preview-strip"
          >
            {previewErrored ? (
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                Preview unavailable — save to see projected earnings.
              </div>
            ) : previewRows ? (
              previewRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    padding: "8px 12px",
                    background: "#F0FDF4",
                    borderRadius: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                  }}
                  data-testid={`partner-pricing-wizard-preview-${row.dog_count}-${row.frequency}`}
                >
                  <span style={{ color: "#4b5563" }}>{row.label}</span>
                  <span style={{ fontWeight: 700, color: "#065f46" }}>
                    you'd earn ${centsToDollars(row.provider_take_amount_cents)}
                    /booking
                  </span>
                </div>
              ))
            ) : (
              <div style={{ color: "#9ca3af", fontSize: 13 }}>
                Computing…
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
function Step2Tier({ tiers, selectedTierId, setSelectedTierId }) {
  return (
    <div
      style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" }}
      data-testid="partner-pricing-wizard-step-2"
    >
      {TIER_CARDS.map((meta) => {
        const t = tiers.find((x) => x.id === meta.id) || {};
        const Icon = meta.icon;
        const selected = selectedTierId === meta.id;
        return (
          <button
            key={meta.id}
            onClick={() => setSelectedTierId(meta.id)}
            style={{
              padding: 18,
              borderRadius: 16,
              background: selected ? "#FFF8EE" : "#ffffff",
              border: `2px solid ${selected ? meta.color : "#e5e7eb"}`,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 120ms",
            }}
            data-testid={`partner-pricing-wizard-tier-card-${meta.id}`}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <Icon size={22} color={meta.color} />
              <span style={{ fontWeight: 800, color: "#374751", fontSize: 18 }}>
                {meta.label}
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
              {meta.tagline}
            </p>
            <div style={{ fontSize: 14, color: "#374751", marginBottom: 6 }}>
              <strong>${centsToDollars(t.subscription_price_cents ?? 0)}</strong>/month
            </div>
            <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 2 }}>
              {t.commission_pct}% commission per booking
            </div>
            <div style={{ fontSize: 13, color: "#4b5563" }}>
              Up to {t.service_area_cap ?? {"standard": 5, "preferred": 10, "elite": 25}[meta.id]} ZIPs
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Step3Review({ pricingRows, selectedTier, confirming, onConfirm }) {
  const isStandard = selectedTier?.id === "standard";
  return (
    <Card
      style={{ border: "1px solid #e5e7eb", borderRadius: 16 }}
      data-testid="partner-pricing-wizard-step-3"
    >
      <CardHeader>
        <CardTitle style={{ color: "#374751" }}>Review + confirm</CardTitle>
      </CardHeader>
      <CardContent style={{ display: "grid", gap: 20 }}>
        <div>
          <p style={{ fontWeight: 700, marginBottom: 8, color: "#374751" }}>
            Your pricing
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            {pricingRows.map((row) => (
              <div
                key={row.dogs}
                style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}
              >
                <span style={{ color: "#4b5563" }}>{row.dogs}</span>
                <span style={{ fontWeight: 600, color: "#374751" }}>
                  ${row.totalDollars.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
        {selectedTier && (
          <div>
            <p style={{ fontWeight: 700, marginBottom: 8, color: "#374751" }}>
              Selected tier
            </p>
            <div style={{ fontSize: 14, color: "#374751" }}>
              <div><strong>{selectedTier.name || selectedTier.id}</strong></div>
              <div>${centsToDollars(selectedTier.subscription_price_cents ?? 0)}/month subscription</div>
              <div>{selectedTier.commission_pct}% commission per booking</div>
            </div>
          </div>
        )}
        {!isStandard && (
          <div
            style={{
              padding: "10px 12px",
              background: "#EFF6FF",
              border: "1px solid #93C5FD",
              borderRadius: 8,
              color: "#1E40AF",
              fontSize: 13,
            }}
            data-testid="partner-pricing-wizard-billing-notice"
          >
            Real subscription billing is coming soon. Admin will
            activate your tier manually in the meantime — you'll get an
            email when it's live.
          </div>
        )}
        <Button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="partner-pricing-wizard-confirm-btn"
          style={{ alignSelf: "flex-start" }}
        >
          {confirming ? (
            <Loader2 className="animate-spin" size={16} />
          ) : isStandard ? (
            "Confirm and go live"
          ) : (
            "Confirm and complete onboarding"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
